/* istanbul ignore file */
import _ from 'underscore';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import React, { useCallback, useContext, useEffect, useState } from 'react';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import { useResponsiveSize } from '@adscore/with-responsive-size';
import { Constants } from '@bingads-webui-theme-2018/theme-fluent';
import { useDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { UiBlocker } from '@adsui/ui-blocker';
import { isSDK } from '@bingads-webui-campaign-react/sdk-utils';
import {
  TileHeader,
  CreationMethodSubTitle,
} from '@bingads-webui-campaign-react/goals-and-website-tile';
import { CampaignCreationMethodSelection, CampaignCreationMethod } from '@bingads-webui-campaign-react/campaign-creation-method-selection';
import { PerfMarkerFactory } from '@bingads-webui/performance-marker';
import { ServerContext } from '@bingads-webui-campaign-react/server-context';
import { usePilotsValue } from '@bingads-webui-campaign-react/basic-context-utils';
import { PageReadyTrigger } from '@bingads-webui-campaign-react/page-ready-trigger';
import { PageReady } from '@bingads-webui/page-ready';
import { useLogEnteredPage, SIGNUP_FLOW } from '@bingads-webui-campaign-react/shared-app-capi';
import { omniturePageLoad } from '@bingads-webui-campaign-react/wizard-utils';
import { stepToPage, getSubFlowFromCurrentFlow, PMAX_LITE_WIZARD_STEP_PATHS } from '@bingads-webui-campaign-react/pmax-lite-wizard-utils';
import { useCampaignCreationMethodRepository } from './use-campaign-creation-method-repository';
import { getIsolatedStepStyles, getSDKIsolatedStepStyles } from './isolated-step-styles';

export const CampaignCreationIsolatedStep = ({
  onStepSelection, supportComponents, currentFlow, hiddenMethod, className,
}) => {
  const classes = useDefaultStyles(getIsolatedStepStyles);
  const i18n = useContext(I18nContext);
  const serverContext = useContext(ServerContext);
  const { CurrentAccount, Identity } = serverContext;
  const firstName = Identity && Identity.FirstName;

  const {
    inlineSupport: InlineSupport,
  } = supportComponents;

  const campaignCreationStepMarker = PerfMarkerFactory.createMarker('PMaxLiteWizard.CAMPAIGN_CREATION_ISOLATED');
  campaignCreationStepMarker.willInit();

  const { save } = useCampaignCreationMethodRepository();
  const [isSaving, setIsSaving] = useState(false);

  const displayMode = useResponsiveSize(Constants.ResponsiveSizeBreakpoints);
  const isChinaSignup = usePilotsValue('isCNMarketExpansionEnabled');

  let tileHeaderText = {
    title: i18n.getString(_TL_('Hi {{firstName}}, how can we help you get started?'), { firstName }),
    subtitle: _TL_('You can create a new account and campaign, or you can import existing campaigns from Google Ads or Meta Ads.'),
  };
  if (isChinaSignup) {
    tileHeaderText = {
      title: i18n.getString(_TL_('Hi {{firstName}}'), { firstName }),
      subtitle: _TL_('You can create a new account'),
    };
  } else if (isSDK()) {
    tileHeaderText = {
      title: i18n.getString(_TL_('Hi {{firstName}}, how can we help you get started?'), { firstName }),
      subtitle: _TL_('You can create a new account and campaign, or you can import existing campaigns from Google Ads.'),
    };
  }

  const goNextWthAccountCreated = useCallback((stepId, flowFactors) => {
    if (!CurrentAccount) {
      setIsSaving(true);
      save().then(() => {
        onStepSelection(stepId, flowFactors);
      }).finally(() => {
        setIsSaving(false);
      });
    } else {
      onStepSelection(stepId, flowFactors);
    }
  }, [CurrentAccount, save, onStepSelection]);

  const onClickCreateNewCampaign = useCallback(() => {
    const nextStep = isChinaSignup
      ? PMAX_LITE_WIZARD_STEP_PATHS.ACCOUNT_DETAILS : PMAX_LITE_WIZARD_STEP_PATHS.LANGUAGE_COUNTRY;

    const flowFactors = {
      isGoogleImport: false,
      isFacebookImport: false,
      isChinaSignup,
    };

    goNextWthAccountCreated(
      nextStep,
      flowFactors
    );
  }, [goNextWthAccountCreated, isChinaSignup]);

  const onClickGoogleImport = useCallback(() => {
    const flowFactors = {
      isGoogleImport: true,
      isFacebookImport: false,
    };
    goNextWthAccountCreated(
      PMAX_LITE_WIZARD_STEP_PATHS.GOOGLE_IMPORT,
      flowFactors
    );
  }, [goNextWthAccountCreated]);

  const onClickFacebookImport = useCallback(() => {
    const flowFactors = {
      isGoogleImport: false,
      isFacebookImport: true,
    };
    goNextWthAccountCreated(
      PMAX_LITE_WIZARD_STEP_PATHS.FACEBOOK_IMPORT,
      flowFactors
    );
  }, [goNextWthAccountCreated]);

  useEffect(() => {
    _.delay(() => {
      PageReady.pageIsReady();
    }, 15000);
  }, []);

  useEffect(() => {
    if (campaignCreationStepMarker) {
      campaignCreationStepMarker.done();
    }
  }, [campaignCreationStepMarker]);

  campaignCreationStepMarker.willRender();

  const currentStepId = PMAX_LITE_WIZARD_STEP_PATHS.CAMPAIGN_CREATION_METHOD;
  const flow = SIGNUP_FLOW.PmaxLiteSignup;
  const subFlow = getSubFlowFromCurrentFlow(currentFlow);

  useLogEnteredPage({ Flow: flow, SubFlow: subFlow, Page: stepToPage[currentStepId] });

  useEffect(() => {
    omniturePageLoad({
      isSignUp: true,
      pageId: stepToPage[currentStepId] || 'unknown step',
      pageTitle: currentStepId,
      signUpPrefix: 'pmax_lite_signup_step_',
    });
  }, [currentStepId]);

  return (
    <div className={classNames('card-layout', classes.cardLayout, className)}>
      <UiBlocker blocking={isSaving} loadingText={i18n.getString(_TL_('Saving...'))}>
        <div className={classNames('main-container', classes.mainContainer)} role="main">
          <div className={classNames('card-container', classes.cardContainer, classes[`cardContainer-${displayMode}`])}>
            <div className={classNames('card-content-container', classes.cardContentContainer)}>
              <TileHeader
                title={tileHeaderText.title}
                subtitle={<CreationMethodSubTitle
                  i18n={i18n}
                  subTitle={tileHeaderText.subtitle}
                />}
                displayMode={displayMode}
              />
              <CampaignCreationMethodSelection
                i18n={i18n}
                onClickCreateNewCampaign={onClickCreateNewCampaign}
                onClickGoogleImport={onClickGoogleImport}
                onClickFacebookImport={onClickFacebookImport}
                isVertical={displayMode === 's' || displayMode === 'm'}
                disableImports={false}
                isRebranding
                isHideImports={isChinaSignup}
                hiddenMethod={hiddenMethod}
              />
              <PageReadyTrigger />
            </div>
            {
              !isChinaSignup &&
              <div className={classNames('tail-container', classes.tailContainer)}>
                <InlineSupport />
              </div>
            }
          </div>
        </div>
      </UiBlocker>
    </div>
  );
};

CampaignCreationIsolatedStep.propTypes = {
  onStepSelection: PropTypes.func.isRequired,
  supportComponents: PropTypes.shape({
    inlineSupport: PropTypes.func.isRequired,
  }).isRequired,
  currentFlow: PropTypes.arrayOf(PropTypes.string).isRequired,
  hiddenMethod: PropTypes.arrayOf(PropTypes.string),
  className: PropTypes.string,
};

CampaignCreationIsolatedStep.defaultProps = {
  hiddenMethod: [],
  className: undefined,
};

const hiddenSDKImportMethod = [
  CampaignCreationMethod.FACEBOOK_IMPORT,
];

export const SDKCampaignCreationIsolatedStep = (props) => {
  const classes = useDefaultStyles(getSDKIsolatedStepStyles);

  return (
    <CampaignCreationIsolatedStep
      {...props}
      hiddenMethod={hiddenSDKImportMethod}
      className={classNames('sdk-campaign-creation-isolated-step', classes.cardLayout)}
    />
  );
};
